import { ApiResult } from 'src/Libs/api'
import { AuthFetchWithResponse } from 'src/Types/api'
import accountLinksToModelTransformer from '../transformer/account-links-to-model.transformer'
import { AccountLinkListDto, AccountLinksUpdateDto } from 'src/Libs/installation-rights-management/dto'
import { AccountLinkModel } from 'src/Libs/installation-rights-management/models'
import { FetchProps } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { handleFetchError } from 'src/Libs/api/utils'

/**
 *
 * Returns all AccountLinks for a single Installation
 *
 * @param installationId
 * @param authFetch
 */
const findAll = async (installationId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<AccountLinkModel[]>> => {
  const fetchProps = {
    path: `installations/${installationId}/account-links`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    payload: {
      sort: '+accountType,+role',
    },
  }

  try {
    const response = await authFetch<void>(fetchProps)

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<AccountLinkModel[]>([], false)
    }
    if (response.status === StatusCodes.OK) {
      const models = accountLinksToModelTransformer(response.data as unknown as AccountLinkListDto)
      return new ApiResult<AccountLinkModel[]>(models, false)
    }

    return new ApiResult<AccountLinkModel[]>(null, true)
  } catch (error) {
    return handleFetchError<AccountLinkModel[]>(error)
  }
}

/**
 * Updates the AccountLink information for to a single Installation.
 *
 * @param installationId
 * @param accountLinkId
 * @param updateDto
 * @param authFetch
 */
const update = async (
  installationId: number,
  accountLinkId: number,
  updateDto: AccountLinksUpdateDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  const fetchProps = {
    path: `installations/${installationId}/account-links/${accountLinkId}`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    method: 'put',
    payload: updateDto,
  } as FetchProps

  try {
    const response = await authFetch<void>(fetchProps)

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult(null, false)
    }
    return new ApiResult(null, true)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}

interface RemoveOneParams {
  installationId: number
  accountLinkId?: number
  authFetch: AuthFetchWithResponse
  reason?: string
}

/**
 * Removes a linked account from an Installation.
 *
 * @param installationId - The Installation ID
 * @param accountLinkId - The AccountLink ID
 * @param authFetch - AuthFetch function from hook
 * @param reason - Optional reason for removal, uses on backend to create installation history records
 */
const removeOne = async ({ accountLinkId, authFetch, installationId, reason }: RemoveOneParams): Promise<ApiResult<void>> => {
  const fetchProps = {
    path: `installations/${installationId}/account-links/${accountLinkId}${reason ? '?reason=' + reason : ''}`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    method: 'delete',
  } as FetchProps

  if (!accountLinkId) {
    return new ApiResult<void>(null, true, 'no account-link id provided')
  }

  try {
    const response = await authFetch<void>(fetchProps)

    if (response.status === StatusCodes.CREATED || response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<void>(null, false)
    }
    return new ApiResult<void>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

const allApis = { findAll, update, removeOne }

export default allApis
